import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Wifi, Smartphone, Zap, AlertCircle, Plus, Edit2, Trash2, ArrowLeft } from 'lucide-react'

export default function DeviceConnectivityPage() {
  const navigate = useNavigate()
  const [devices, setDevices] = useState([
    { id: 1, name: 'Desktop PC-01', type: 'Desktop', mac: '00:1A:2B:3C:4D:5E', ip: '192.168.1.50', status: 'Connected', signal: 'Strong', lastSeen: '2 min ago' },
    { id: 2, name: 'Laptop-Corporate', type: 'Laptop', mac: '00:1A:2B:3C:4D:5F', ip: '192.168.1.51', status: 'Connected', signal: 'Good', lastSeen: '1 min ago' },
    { id: 3, name: 'Server-01', type: 'Server', mac: '00:1A:2B:3C:4D:60', ip: '192.168.1.100', status: 'Connected', signal: 'Strong', lastSeen: 'Just now' },
    { id: 4, name: 'Printer-Floor2', type: 'Printer', mac: '00:1A:2B:3C:4D:61', ip: '192.168.1.75', status: 'Idle', signal: 'Weak', lastSeen: '15 min ago' },
    { id: 5, name: 'Mobile-Phone', type: 'Mobile', mac: '00:1A:2B:3C:4D:62', ip: '192.168.1.52', status: 'Disconnected', signal: 'None', lastSeen: '2 hours ago' },
  ])

  const [showModal, setShowModal] = useState(false)
  const [formData, setFormData] = useState({ name: '', type: 'Desktop', mac: '', ip: '' })
  const [editingDevice, setEditingDevice] = useState(null)

  const stats = useMemo(() => ({
    totalDevices: devices.length,
    connected: devices.filter(d => d.status === 'Connected').length,
    idle: devices.filter(d => d.status === 'Idle').length,
    disconnected: devices.filter(d => d.status === 'Disconnected').length
  }), [devices])

  const handleCreate = () => {
    setEditingDevice(null)
    setFormData({ name: '', type: 'Desktop', mac: '', ip: '' })
    setShowModal(true)
  }

  const handleEdit = (device) => {
    setEditingDevice(device)
    setFormData({ name: device.name, type: device.type, mac: device.mac, ip: device.ip })
    setShowModal(true)
  }

  const handleSave = () => {
    if (!formData.name || !formData.mac || !formData.ip) return alert('All fields required')
    if (editingDevice) {
      setDevices(devices.map(d => d.id === editingDevice.id ? { ...d, ...formData } : d))
    } else {
      setDevices([...devices, { id: Date.now(), ...formData, status: 'Connected', signal: 'Good', lastSeen: 'Just now' }])
    }
    setShowModal(false)
  }

  const handleDelete = (id) => {
    if (confirm('Remove this device?')) {
      setDevices(devices.filter(d => d.id !== id))
    }
  }

  const getSignalColor = (signal) => {
    switch (signal) {
      case 'Strong': return 'text-green-400'
      case 'Good': return 'text-blue-400'
      case 'Weak': return 'text-yellow-400'
      case 'None': return 'text-red-400'
      default: return 'text-slate-400'
    }
  }

  const getDeviceIcon = (type) => {
    switch (type) {
      case 'Desktop':
        return <Wifi className="text-blue-400" size={20} />
      case 'Laptop':
        return <Zap className="text-blue-400" size={20} />
      case 'Server':
        return <AlertCircle className="text-blue-400" size={20} />
      case 'Mobile':
        return <Smartphone className="text-blue-400" size={20} />
      case 'Printer':
        return <AlertCircle className="text-blue-400" size={20} />
      default:
        return <Wifi className="text-blue-400" size={20} />
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="max-w-7xl mx-auto">
        <button onClick={() => navigate('/network')} className="flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-6 font-semibold">
          <ArrowLeft size={20} /> Back to Network Management
        </button>

        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-4xl font-bold text-white mb-2">Device Connectivity</h1>
              <p className="text-slate-400">Monitor connected devices and network access</p>
            </div>
            <button onClick={handleCreate} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold">
              <Plus size={20} /> Add Device
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Total Devices</div>
            <div className="text-3xl font-bold text-blue-400">{stats.totalDevices}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-green-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Connected</div>
            <div className="text-3xl font-bold text-green-400">{stats.connected}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-yellow-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Idle</div>
            <div className="text-3xl font-bold text-yellow-400">{stats.idle}</div>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-red-500/20 p-6">
            <div className="text-slate-400 text-sm mb-2">Disconnected</div>
            <div className="text-3xl font-bold text-red-400">{stats.disconnected}</div>
          </div>
        </div>

        <div className="space-y-4">
          {devices.map((device) => (
            <div key={device.id} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {getDeviceIcon(device.type)}
                    <h3 className="text-lg font-semibold text-white">{device.name}</h3>
                    <span className={`px-3 py-1 rounded text-xs font-semibold ${device.status === 'Connected' ? 'bg-green-600/20 text-green-400' : device.status === 'Idle' ? 'bg-yellow-600/20 text-yellow-400' : 'bg-red-600/20 text-red-400'}`}>{device.status}</span>
                  </div>

                  <div className="grid grid-cols-4 gap-4 mt-4">
                    <div className="bg-slate-700/30 rounded p-3">
                      <p className="text-slate-400 text-xs">Device Type</p>
                      <p className="text-white font-bold mt-1">{device.type}</p>
                    </div>
                    <div className="bg-slate-700/30 rounded p-3">
                      <p className="text-slate-400 text-xs">IP Address</p>
                      <p className="text-white font-bold mt-1 font-mono text-sm">{device.ip}</p>
                    </div>
                    <div className="bg-slate-700/30 rounded p-3">
                      <p className="text-slate-400 text-xs">Signal</p>
                      <p className={`font-bold mt-1 ${getSignalColor(device.signal)}`}>{device.signal}</p>
                    </div>
                    <div className="bg-slate-700/30 rounded p-3">
                      <p className="text-slate-400 text-xs">Last Seen</p>
                      <p className="text-white font-bold mt-1">{device.lastSeen}</p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(device)} className="p-2 hover:bg-blue-500/20 rounded">
                    <Edit2 size={18} className="text-blue-400" />
                  </button>
                  <button onClick={() => handleDelete(device.id)} className="p-2 hover:bg-red-500/20 rounded">
                    <Trash2 size={18} className="text-red-400" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 p-8 max-w-2xl w-full mx-4">
              <h2 className="text-2xl font-bold text-white mb-6">{editingDevice ? 'Edit Device' : 'Add Device'}</h2>
              <div className="space-y-4 mb-6">
                <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Device Name" className="w-full bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white placeholder-slate-500" />
                <input type="text" value={formData.mac} onChange={(e) => setFormData({...formData, mac: e.target.value})} placeholder="MAC Address" className="w-full bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white placeholder-slate-500" />
                <div className="grid grid-cols-2 gap-4">
                  <input type="text" value={formData.ip} onChange={(e) => setFormData({...formData, ip: e.target.value})} placeholder="IP Address" className="bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white" />
                  <select value={formData.type} onChange={(e) => setFormData({...formData, type: e.target.value})} className="bg-slate-700 border border-blue-500/20 rounded px-4 py-2 text-white">
                    <option>Desktop</option>
                    <option>Laptop</option>
                    <option>Server</option>
                    <option>Mobile</option>
                    <option>Printer</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowModal(false)} className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded font-semibold">Cancel</button>
                <button onClick={handleSave} className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold">Save</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
